import { AfterViewInit, Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR, NgControl, Validators } from '@angular/forms';
import { GateDefinitionEnum } from "@shared-features/enums/gate-definition.enum";
import { GateDefinition } from '@shared/model/gate-definition.model';
import { getValueByPath } from '@shared/utils/get-value-by-path.util';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-gate-definition',
  templateUrl: './gate-definition.component.html',
  styleUrls: ['./gate-definition.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: GateDefinitionComponent,
    },
  ],
})
export class GateDefinitionComponent implements ControlValueAccessor,OnInit, AfterViewInit, OnDestroy {
  gateDefinitionForm:FormGroup
  destroy$ = new Subject();

  protected readonly gateDefinitionEnum = GateDefinitionEnum
  ngControl: NgControl;
  requiredKeys: string[] = [];
  touched: {[key: string]: boolean } = {
    type: false,
    name: false,
  };

  initialValue: GateDefinition;

  get value(): GateDefinition {
    return this.gateDefinitionForm?.value;
  }
  onChange = (value: GateDefinition) => {};
  onTouched = () => {};

  constructor(
    private injector: Injector,
    private formBuilder: FormBuilder,
  ) { }

  ngOnInit() {
    this.initForm()
  }

  initForm(){
    const requiredKeys = ['type', 'name'];

    this.gateDefinitionForm = this.formBuilder.group({
      type:[ GateDefinitionEnum.InBound , Validators.required],
      temperatureControl:[false],
      name:["", Validators.required],
      reference:[""],
      truckClassify:[null]
    });
    const truckClassify = this.gateDefinitionForm.get('truckClassify');

    this.requiredKeys = requiredKeys;

    this.gateDefinitionForm.valueChanges
      .pipe(takeUntil(this.destroy$), debounceTime(50)).subscribe((value) => {
        if(value.temperatureControl){
          truckClassify.setValidators([Validators.required]);
        }else{
          truckClassify.setValue(null)
          truckClassify.clearValidators();
        }
        truckClassify.updateValueAndValidity();

        if (this.requiredKeysHaveValue()) {
          this.onChange(value);
        } else {
          this.onChange(null);
        }
        if (this.gateDefinitionForm.invalid && this.ngControl) {
          this.ngControl.control.setErrors({ ...this.gateDefinitionForm.errors });
        }
      });
  }


  ngAfterViewInit(): void {
    this.ngControl = this.injector.get(NgControl);
    const originalMarkAsTouched = this.ngControl.control.markAsTouched.bind(this.ngControl.control);
    this.ngControl.control.markAsTouched = (...args) => {
      this.markAsTouched();
      return originalMarkAsTouched(...args);
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  registerOnChange(onChange: any): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any): void {
    this.onTouched = onTouched;
  }

  writeValue(value: GateDefinition): void {
    this.initialValue = Object.assign({}, value);
    this.gateDefinitionForm.patchValue({ ...this.gateDefinitionForm.value, ...value }, { onlySelf: true , emitEvent: false });
  }

  markAsTouched(): void {
    this.gateDefinitionForm.markAllAsTouched();
  }

  handleTouch(key: string): void {
    this.touched[key] = true;
    this.checkIfAllFieldsTouched();
  }

  checkIfAllFieldsTouched(): void {
    const keys = Object.keys(this.touched)
      .filter(key => this.touched[key] !== null && typeof this.touched[key] !== 'undefined');
    if (keys.every(key => this.touched[key])) {
      this.onTouched();
    }
  }

  private requiredKeysHaveValue(): boolean {
    return this.requiredKeys.map((key) => getValueByPath(this.value, key))
      .every((v) => typeof v !== 'undefined' && v !== null);
  }

}
